import type { LitSquadCraftIngredient } from '@/lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CraftIngredientSelectProps {
  label: string;
  quantity: number;
  required: boolean;
  value: string;
  onChange: (value: string) => void;
  ingredients: LitSquadCraftIngredient[];
  disabled?: boolean;
}

/**
 * Sentinel value used by the Select to represent "no ingredient
 * selected". Required because Radix Select rejects empty-string values
 * and because passing `value={undefined}` would toggle between
 * controlled and uncontrolled modes (React warns on that).
 */
const NONE_SENTINEL = '__none__';

/**
 * One slot selector in the crafting UI. Each option shows the item name
 * and the user's aggregated count; options whose count < `quantity` are
 * marked insufficient but still selectable (so users can see what they
 * would need to acquire).
 */
export function CraftIngredientSelect({
  label,
  quantity,
  required,
  value,
  onChange,
  ingredients,
  disabled = false,
}: CraftIngredientSelectProps) {
  const placeholder = required ? `Select ${label}…` : `Optional — ${label}`;

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium flex items-center gap-2">
        {label}
        <span className="text-xs text-muted-foreground">
          × {quantity}
          {required ? '' : ' (optional)'}
        </span>
      </label>
      <Select
        value={value || NONE_SENTINEL}
        onValueChange={(v) => onChange(v === NONE_SENTINEL ? '' : v)}
        disabled={disabled}
      >
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_SENTINEL}>{required ? placeholder : '— None —'}</SelectItem>
          {ingredients.length === 0 && (
            <SelectItem value="__empty__" disabled>
              No eligible items
            </SelectItem>
          )}
          {ingredients.map((ing) => {
            const insufficient = ing.userCount < quantity;
            return (
              <SelectItem
                key={ing.name}
                value={ing.name}
                className={insufficient ? 'opacity-60' : ''}
              >
                <span className="flex items-center justify-between gap-3 w-full">
                  <span>{ing.name}</span>
                  <span
                    className={`text-xs ${insufficient ? 'text-destructive' : 'text-muted-foreground'}`}
                  >
                    You have {ing.userCount}
                  </span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
